import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { store } from "../../../../../store";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemTaint } from "../aws/CpArrayItemTaint";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { EllipsisOutlined } from "@ant-design/icons";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { NodePoolDetectedTags } from "../detectedTags";
import { NodePoolCoreButton } from "../nodePoolCoreButton";

interface Props {
  index: number;
}
export const HetznerNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const region = useCpFormValue(`hetzner.region`);
  const name = useCpFormValue(`hetzner.nodePools.${index}.name`);
  const serverType = useCpFormValue(`hetzner.nodePools.${index}.serverType`);
  const overrideImage = useCpFormValue(`hetzner.nodePools.${index}.overrideImage`);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool{name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={[`hetzner.nodePools`]} />
          </CardTitle>
          <CardContent>
            <NodePoolDetectedTags name={`hetzner.nodePools.${index}.labels`} />
            <CpFormItem>
              <CpLabel name={`hetzner.nodePools.${index}.name`}>Name</CpLabel>
              <CpInput name={`hetzner.nodePools.${index}.name`} style={{ width: 600 }} />
              <CpErrorWarning name={`hetzner.nodePools.${index}.name`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`hetzner.nodePools.${index}.serverType`}>Server Type</CpLabel>
              <Space.Compact>
                <CpInput name={`hetzner.nodePools.${index}.serverType`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("serverType")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`hetzner.nodePools.${index}.serverType`} />
            </CpFormItem>
            <CpFormItem>
              <div className="flex gap-2">
                <div className="flex flex-col">
                  <CpLabel name={`hetzner.nodePools.${index}.minSize`}>Min Size</CpLabel>
                  <CpInputNumber name={`hetzner.nodePools.${index}.minSize`} />
                </div>
                <div className="flex flex-col">
                  <CpLabel name={`hetzner.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                  <CpInputNumber name={`hetzner.nodePools.${index}.maxSize`} />
                </div>
              </div>
              <CpErrorWarningArray
                names={[`hetzner.nodePools.${index}.minSize`, `hetzner.nodePools.${index}.maxSize`]}
              />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`hetzner.nodePools.${index}.overrideImage`}>Override Image</CpLabel>
              <Space.Compact>
                <CpInput name={`hetzner.nodePools.${index}.overrideImage`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("overrideImage")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`hetzner.nodePools.${index}.overrideImage`} />
            </CpFormItem>
            <div style={{ maxWidth: 900 }}>
              <CpFormItem>
                <div className="flex items-center gap-4">
                  <CpLabel name={`skip`}>Labels</CpLabel>
                  <NodePoolCoreButton name={`hetzner.nodePools.${index}.labels`} />
                </div>
                <div className="-mt-2">
                  <CpArrayObject
                    factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                    labels={["Name", "Value"]}
                    name={`hetzner.nodePools.${index}.labels`}
                  >
                    <CpArrayItemNameValuePair />
                  </CpArrayObject>
                </div>
              </CpFormItem>
            </div>
            <div style={{ maxWidth: 900 }}>
              <CpArrayObject
                factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                labels={["Key", "Value", "Effect"]}
                label="Taints"
                name={`hetzner.nodePools.${index}.taints`}
              >
                <CpArrayItemTaint />
              </CpArrayObject>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "overrideImage" ? (
        <NGProviderBrowser
          title="Browse Hetzner Images"
          initialValue={overrideImage}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: inputToMk8s(values),
              },
            });
            const _images = data.images.sort((a: any, b: any) => {
              if (a.name > b.name) {
                return 1;
              }
              if (a.name < b.name) {
                return -1;
              }
              return 0;
            });

            const images: any[] = [];

            for (const image of _images) {
              const foundImage = images.find((i) => i.name === image.name);
              if (foundImage) {
                if (!foundImage.architecture.includes(image.architecture)) {
                  foundImage.architecture.push(image.architecture);
                }
              } else {
                image.architecture = [image.architecture];
                images.push(image);
              }
            }
            return images;
          }}
          onOk={(value) => {
            formSetValue(formId, `hetzner.nodePools.${index}.overrideImage`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description", "Disk Size", "Architecture"]}
          getData={(item: any) => [
            item.name,
            item.name,
            item.description,
            formatSizeForProviderBrowser(item.disk_size),
            item.architecture.join(", "),
          ]}
        />
      ) : null}
      {browserKey === "serverType" ? (
        <NGProviderBrowser
          title="Browse Hetzner Server Types"
          initialValue={serverType}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;
            // TODOMK8S paginate
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "server_types",
                cluster: inputToMk8s(values),
              },
            });
            let items = data.server_types;
            items = items.filter((i: any) => (i.prices as any[]).some((p) => p.location === region));
            return items;
          }}
          onOk={(value) => {
            formSetValue(formId, `hetzner.nodePools.${index}.serverType`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          filteredColumns={["Name"]}
          distinctFilters={["CPU Type", "Cores", "Memory", "Architecture", "Disk", "Included Traffic"]}
          initialSort={["Cost per Month", "asc"]}
          rightAlign={["Cost per Month"]}
          monoColumns={["Cost per Month"]}
          labels={["Name", "Cost per Month", "CPU Type", "Cores", "Memory", "Architecture", "Disk", "Included Traffic"]}
          sortable
          getData={(item: any) => {
            let includedTraffic = "N/A";

            let cost = "N/A";
            const priceItem = (item.prices as any[]).find((p) => p.location === region);
            let price = 0;
            if (priceItem) {
              price = Number(priceItem.price_monthly.net);
              cost = price.toLocaleString("en-US", { style: "currency", currency: "USD" });
              includedTraffic = (priceItem.included_traffic / 1_024 / 1_024 / 1_024 / 1_024).toFixed(0);
            }

            return [
              item.name,
              item.name,
              [price, cost],
              item.cpu_type,
              item.cores,
              [item.memory, formatSizeForProviderBrowser(item.memory)],
              item.architecture,
              [item.disk, formatSizeForProviderBrowser(item.disk)],
              [includedTraffic, `${includedTraffic}TB`],
            ];
          }}
        />
      ) : null}
    </>
  );
};
